import { useEffect, useCallback, useMemo, useState } from 'react';
import {
  Box,
  VStack,
  Spinner,
  HStack,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  MenuDivider,
  Flex,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { BaseWidgetProps } from '../widgets';
import { WidgetWrapper } from '../shared/components/WidgetWrapper';
import { PaLeadTimeDistributionOptions, WidgetType } from '../shared/types';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { statisticsApi } from '@texas/api/endpoints/statisticsApi';
import { DistributionChart } from './DistributionChart';
import { StatisticsHeaderFilters } from '../shared/StatisticsHeaderFilters';
import {
  SearchableMenuInput,
  SearchableMenuOption,
} from '@texas/components/shared/search/SearchableMenu';
import { useWidgetStorage } from '../shared/useWidgetStorage';
import { Icons } from '@texas/components/shared/Icons';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { ProductApprovalState } from '@texas/api/endpoints/productApprovalsApi';

interface Props extends BaseWidgetProps {
  options?: PaLeadTimeDistributionOptions;
}

const paStateOptions = [
  { label: 'Pending', value: ProductApprovalState.Pending },
  { label: 'Accepted', value: ProductApprovalState.Accepted },
  { label: 'Rejected', value: ProductApprovalState.Rejected },
  { label: 'Cancelled', value: ProductApprovalState.Cancelled },
  { label: 'On Hold', value: ProductApprovalState.OnHold },
];

export function PaLeadTimeDistributionWidget({
  color,
  customTitle,
  widgetId,
  options,
}: Props) {
  const { t } = useTranslation();
  const {
    filters,
    updateSampleSize,
    updateCustomerIds,
    updateBranchIds,
    updatePaState,
    updateSupplierId,
  } = useWidgetStorage(widgetId);

  const [supplierSearchInput, setSupplierSearchInput] = useState('');

  const {
    suppliersData: [suppliers, getSuppliers, loadingSuppliers],
  } = useMetadata();

  const { data, loading, refetch } = useApiResource(
    statisticsApi.getPaLeadTimeDistribution,
  );

  const selectedCustomerIds =
    options?.customerIds && options.customerIds.length > 0
      ? options.customerIds
      : filters.customerIds;
  const selectedBranchIds =
    options?.branchIds && options.branchIds.length > 0
      ? options.branchIds
      : filters.branchIds;

  useEffect(() => {
    getSuppliers();
  }, [getSuppliers]);

  useEffect(() => {
    refetch({
      sampleSize: filters.sampleSize,
      customerIds:
        selectedCustomerIds.length > 0 ? selectedCustomerIds : undefined,
      branchIds: selectedBranchIds.length > 0 ? selectedBranchIds : undefined,
      state: filters.paState ?? undefined,
      supplierId: filters.supplierId ?? undefined,
    });
  }, [
    filters.sampleSize,
    selectedCustomerIds,
    selectedBranchIds,
    filters.paState,
    filters.supplierId,
    refetch,
  ]);

  const stateDisplayText = useCallback(() => {
    if (filters.paState === null) return t('filter.allStates');
    const state = paStateOptions.find((s) => s.value === filters.paState);
    return state?.label ?? '';
  }, [filters.paState, t]);

  const supplierDisplayText = useCallback(() => {
    if (filters.supplierId === null) return 'All Suppliers';
    const supplier = suppliers.find((s) => s.id === filters.supplierId);
    return supplier?.name ?? 'Supplier';
  }, [filters.supplierId, suppliers]);

  const filteredSuppliers = useMemo(() => {
    if (supplierSearchInput.length === 0) return [];
    return suppliers
      .filter(
        (supplier) =>
          supplier.name
            .toLowerCase()
            .includes(supplierSearchInput.toLowerCase()) && !supplier.archived,
      )
      .slice(0, Math.max(30, suppliers.length));
  }, [suppliers, supplierSearchInput]);

  return (
    <WidgetWrapper
      widgetId={widgetId}
      loading={loading}
      customColor={color}
      type={WidgetType.PaLeadTimeDistribution}
      style="default"
      header={customTitle ?? t('widgets.paLeadTimeDistribution.title')}
      toolbox={
        <HStack spacing={2} overflowX="auto">
          <StatisticsHeaderFilters
            sampleSize={filters.sampleSize}
            onSampleSizeChange={updateSampleSize}
            selectedCustomerIds={selectedCustomerIds}
            onCustomerIdsChange={updateCustomerIds}
            selectedBranchIds={selectedBranchIds}
            onBranchChange={updateBranchIds}
            isCustomerLocked={
              !!options?.customerIds && options.customerIds.length > 0
            }
            isBranchLocked={
              !!options?.branchIds && options.branchIds.length > 0
            }
          />
          <Menu isLazy={true}>
            <MenuButton
              as={Button}
              minW="fit-content"
              size="sm"
              variant="ghost"
              rightIcon={<Icons.ChevronDown boxSize={4} />}
            >
              <Text fontSize="sm">{stateDisplayText()}</Text>
            </MenuButton>
            <Portal>
              <MenuList>
                <MenuItem
                  onClick={() => updatePaState(null)}
                  fontWeight={filters.paState === null ? 'bold' : 'normal'}
                >
                  {t('filter.all')}
                </MenuItem>
                <MenuDivider />
                {paStateOptions.map((state) => (
                  <MenuItem
                    key={state.value}
                    onClick={() => updatePaState(state.value)}
                    fontWeight={
                      filters.paState === state.value ? 'bold' : 'normal'
                    }
                  >
                    {state.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>
          <Menu isLazy={true}>
            <MenuButton
              as={Button}
              size="sm"
              minW="fit-content"
              variant="ghost"
              rightIcon={<Icons.ChevronDown boxSize={4} />}
            >
              <Text fontSize="sm">{supplierDisplayText()}</Text>
            </MenuButton>
            <Portal>
              <MenuList maxH="400px" overflowY="auto">
                <SearchableMenuInput
                  placeholder={t('general.search')}
                  value={supplierSearchInput}
                  onChange={setSupplierSearchInput}
                />
                <MenuItem
                  onClick={() => {
                    updateSupplierId(null);
                    setSupplierSearchInput('');
                  }}
                  fontWeight={filters.supplierId === null ? 'bold' : 'normal'}
                >
                  {t('filter.all')}
                </MenuItem>
                <MenuDivider />
                {loadingSuppliers ? (
                  <MenuItem isDisabled={true}>{t('general.loading')}</MenuItem>
                ) : (
                  <Flex flexDir="column" pb={2} gap={1} alignItems="start">
                    {filteredSuppliers.map((supplier) => (
                      <SearchableMenuOption
                        key={supplier.id}
                        label={supplier.name}
                        isSelected={supplier.id === filters.supplierId}
                        showSelectionIndicator={true}
                        onClick={() => updateSupplierId(supplier.id)}
                      />
                    ))}
                  </Flex>
                )}
              </MenuList>
            </Portal>
          </Menu>
        </HStack>
      }
    >
      <VStack align="stretch" spacing={4} height="100%">
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
          >
            <Spinner />
          </Box>
        ) : (
          <DistributionChart data={data?.dayDistributions ?? []} />
        )}
      </VStack>
    </WidgetWrapper>
  );
}
